// Order nodes so a branch stays together.
//
// THE PROBLEM. Y is already spent: every node is pinned to a row by its
// classification rank, because vertical position means recovery order. That
// leaves X, and nothing was constraining it — ELK placed nodes within a row
// purely to minimise crossings globally, so the eight VMs on one cluster ended
// up scattered across their row and every membership edge became a long
// diagonal. The picture was correct and unreadable.
//
// THE LEVER. elk.layered.considerModelOrder is already set to NODES_AND_EDGES,
// which means the ORDER NODES ARE HANDED TO ELK influences placement within a
// layer. So the fix is not a new layout mode or a change to the bands: it is
// feeding ELK the nodes in an order where a subtree is contiguous.
//
// WHAT COUNTS AS A BRANCH. Edges run provider -> dependent, so a node's
// providers sit below it. A node can have several — a VM needs its hypervisor,
// and also DNS, and also the site — but only one of those is what it RUNS ON.
// That one is the NEAREST provider below it, the one with the highest rank. A
// VM at 56 with providers at 48 (hypervisor) and 29 (DNS) belongs to the
// hypervisor; DNS is a shared service it merely consumes. Picking the nearest
// provider turns a many-to-many graph into a tree without asking anyone to
// declare anything.
//
// WHAT THIS DOES NOT FIX. Shared services still cross everything. DNS at 29
// serving forty things is a hub, and no ordering of a row makes a forty-spoke
// hub tidy — that needs the edges hidden, bundled or ghosted, which is a
// separate decision. This makes the ownership structure readable; it does not
// reduce the hub count.

const isGraphNode = (n) =>
    n && n.type !== 'background' && n.type !== 'backgroundText' && n.type !== 'bandRow';

/**
 * Reorder `nodes` so that every subtree occupies a contiguous run.
 *
 * Pure and total: returns a permutation of the input, never drops or duplicates
 * a node, and returns the input unchanged if there is nothing to order. Nodes
 * that are not part of the graph (backgrounds, band rows) keep their original
 * relative order and stay at the front, because React Flow paints in array order
 * and they have to stay behind everything else.
 */
export function orderNodesByBranch(nodes, edges) {
    if (!Array.isArray(nodes) || nodes.length === 0) return nodes;
    const list = Array.isArray(edges) ? edges : [];

    const decor = nodes.filter((n) => !isGraphNode(n));
    const graph = nodes.filter(isGraphNode);
    if (graph.length === 0) return nodes;

    const byId = new Map(graph.map((n) => [n.id, n]));
    const rank = (id) => {
        const v = Number(byId.get(id)?.data?.level);
        return Number.isFinite(v) ? v : -Infinity;
    };

    // provider -> dependent. Ignore self-edges: most objects carry one, and a node
    // that owned itself would never be reached from a root.
    const providersOf = new Map();
    for (const e of list) {
        if (!e || e.source === e.target) continue;
        if (!byId.has(e.source) || !byId.has(e.target)) continue;
        if (!providersOf.has(e.target)) providersOf.set(e.target, []);
        providersOf.get(e.target).push(e.source);
    }

    // The nearest provider below is the one a node structurally belongs to. Ties
    // break on id so the layout is stable between renders — an unstable order
    // would reshuffle the canvas on every refresh for no reason.
    const ownerOf = new Map();
    for (const n of graph) {
        const providers = providersOf.get(n.id);
        if (!providers || providers.length === 0) continue;
        let best = null;
        for (const p of providers) {
            if (p === n.id) continue;
            if (best === null) { best = p; continue; }
            const dp = rank(p);
            const db = rank(best);
            if (dp > db || (dp === db && String(p) < String(best))) best = p;
        }
        // A node whose only provider is itself, or whose owner would sit at or above
        // it, is left as a root rather than creating a cycle.
        if (best !== null && rank(best) < rank(n.id)) ownerOf.set(n.id, best);
    }

    const childrenOf = new Map();
    for (const [child, owner] of ownerOf) {
        if (!childrenOf.has(owner)) childrenOf.set(owner, []);
        childrenOf.get(owner).push(child);
    }
    const sortIds = (ids) =>
        ids.sort((a, b) => rank(a) - rank(b) || String(a).localeCompare(String(b)));
    for (const ids of childrenOf.values()) sortIds(ids);

    const roots = sortIds(graph.filter((n) => !ownerOf.has(n.id)).map((n) => n.id));

    // Iterative DFS. Recursion would be fine at 93 nodes and is not fine at the size
    // a real customer estate reaches.
    const ordered = [];
    const seen = new Set();
    for (const root of roots) {
        if (seen.has(root)) continue;
        const stack = [root];
        while (stack.length) {
            const id = stack.pop();
            if (seen.has(id)) continue;
            seen.add(id);
            ordered.push(byId.get(id));
            const kids = childrenOf.get(id);
            // Pushed in reverse so the sorted order is what comes back off the stack.
            if (kids) for (let i = kids.length - 1; i >= 0; i--) stack.push(kids[i]);
        }
    }

    // Anything unreached — a cycle, or a node whose owner was filtered out — keeps its
    // original order at the end. Dropping it would remove it from the canvas.
    for (const n of graph) if (!seen.has(n.id)) ordered.push(n);

    return [...decor, ...ordered];
}
